<template>
  <div v-if="open" class="tenant-setup-backdrop d-flex align-items-center justify-content-center" @click.self="close">
    <section class="tenant-setup-modal bg-white d-flex flex-column" role="dialog" aria-modal="true">
      <header class="tenant-setup-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="tenant-setup-modal-title mb-2">Edit tenant configuration</h2>
          <div class="tenant-setup-step-copy">Step {{ step }} of 5</div>
        </div>
        <button type="button" class="tenant-setup-close btn border-0 p-0" aria-label="Close" @click="close">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="tenant-setup-body">
        <div v-if="step === 1">
          <h3 class="tenant-setup-section-title mb-1">Choose a tenant</h3>
          <p class="tenant-setup-section-copy mb-4">Select the company whose configuration you want to edit.</p>

          <div class="mb-4">
            <label class="tenant-setup-label mb-2" for="portfolio_tenant_select">Company or tenant</label>
            <select id="portfolio_tenant_select" v-model.number="selected_project_id" class="form-select tenant-setup-control" @change="load_selected_tenant">
              <option :value="null" disabled>Select a tenant</option>
              <option v-for="project in normalized_projects" :key="project.id" :value="Number(project.id)">
                {{ project.name || project.project_name }}
              </option>
            </select>
          </div>

          <template v-if="selected_project_id">
            <h3 class="tenant-setup-section-title mb-1">Tell us about your organisation</h3>
            <p class="tenant-setup-section-copy mb-4">Update the tenant identity and primary contact.</p>

            <div class="mb-3">
              <label class="tenant-setup-label mb-2" for="edit_tenant_name">Organisation name</label>
              <input id="edit_tenant_name" v-model="form.organisation_name" class="form-control tenant-setup-control" type="text">
            </div>
            <div class="mb-3">
              <label class="tenant-setup-label mb-2" for="edit_tenant_sector">Sector</label>
              <input id="edit_tenant_sector" v-model="form.sector" class="form-control tenant-setup-control" type="text">
            </div>
            <div class="mb-3">
              <label class="tenant-setup-label mb-2" for="edit_tenant_contact">Primary contact</label>
              <input id="edit_tenant_contact" v-model="form.primary_contact" class="form-control tenant-setup-control" type="text">
            </div>
            <div class="mb-3">
              <label class="tenant-setup-label mb-2" for="edit_tenant_email">Primary email</label>
              <input id="edit_tenant_email" v-model="form.primary_email" class="form-control tenant-setup-control" type="email">
            </div>
            <div>
              <label class="tenant-setup-label mb-2" for="edit_tenant_phone">Primary phone</label>
              <input id="edit_tenant_phone" v-model="form.primary_phone" class="form-control tenant-setup-control" type="tel">
            </div>
          </template>
        </div>

        <div v-else-if="step === 2">
          <h3 class="tenant-setup-section-title mb-1">Pick your case domains</h3>
          <p class="tenant-setup-section-copy mb-4">Choose the entity configurations available to this tenant.</p>
          <div class="tenant-setup-choice-grid">
            <label v-for="domain in domain_options" :key="domain.key" class="tenant-setup-choice-card" :class="{ 'is-selected': form.domains.includes(domain.key) }">
              <input v-model="form.domains" type="checkbox" :value="domain.key">
              <span>
                <span class="tenant-setup-choice-title">{{ domain.title }}</span>
                <span class="tenant-setup-choice-description">{{ domain.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else-if="step === 3">
          <h3 class="tenant-setup-section-title mb-1">Choose tenant roles</h3>
          <p class="tenant-setup-section-copy mb-4">Select the roles that should have access to this tenant.</p>
          <div class="tenant-setup-choice-grid">
            <label v-for="role in role_options" :key="role.key" class="tenant-setup-choice-card" :class="{ 'is-selected': form.roles.includes(role.key) }">
              <input v-model="form.roles" type="checkbox" :value="role.key">
              <span>
                <span class="tenant-setup-choice-title">{{ role.title }}</span>
                <span class="tenant-setup-choice-description">{{ role.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else-if="step === 4">
          <h3 class="tenant-setup-section-title mb-1">Plug in the basics</h3>
          <p class="tenant-setup-section-copy mb-4">Integration selections remain editable in this wizard.</p>
          <div class="tenant-setup-integration-list">
            <label v-for="integration in integration_options" :key="integration.key" class="tenant-setup-choice-card tenant-setup-integration-card" :class="{ 'is-selected': form.integrations.includes(integration.key) }">
              <input v-model="form.integrations" type="checkbox" :value="integration.key">
              <span>
                <span class="tenant-setup-choice-title">{{ integration.title }}</span>
                <span class="tenant-setup-choice-description">{{ integration.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else>
          <h3 class="tenant-setup-section-title mb-1">All set.</h3>
          <p class="tenant-setup-section-copy mb-4">The tenant configuration has been updated.</p>
          <div class="tenant-setup-provision-note">
            {{ form.organisation_name }} Â· {{ form.domains.length }} domain(s) Â· {{ form.roles.length }} role(s)
          </div>
        </div>
      </div>

      <footer class="tenant-setup-footer d-flex align-items-center justify-content-end gap-2">
        <button type="button" class="btn tenant-setup-secondary-btn" :disabled="saving" @click="step === 1 ? close() : step--">
          {{ step === 1 ? 'Cancel' : 'Back' }}
        </button>
        <button v-if="step < 5" type="button" class="btn btn-primary-dark tenant-setup-primary-btn d-inline-flex align-items-center gap-2" :disabled="saving || loading" @click="continue_step">
          <span v-if="saving || loading" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          {{ saving ? 'Saving...' : 'Continue' }}
        </button>
        <button v-else type="button" class="btn btn-primary-dark tenant-setup-primary-btn" @click="finish">Finish setup</button>
      </footer>
    </section>
  </div>
</template>

<script>
// @ts-check
import alert_mixin from '@/utils/mixins/alert_mixin';
import { normalize_projects } from './tenant_configuration_editor_project_helpers';
import {
  create_editor_state,
  tenant_configuration_editor_methods
} from './tenant_configuration_editor_methods';

export default {
  name: 'TenantConfigurationEditor',
  mixins: [alert_mixin],
  props: {
    open: { type: Boolean, default: false },
    projects: { type: Array, default: () => [] }
  },
  emits: ['close', 'saved'],
  data: () => create_editor_state(),
  computed: {
    normalized_projects() {
      return normalize_projects(this.projects);
    }
  },
  watch: {
    open(value) {
      if (value && typeof this.reset === 'function') this.reset();
    }
  },
  methods: tenant_configuration_editor_methods
};
</script>
